// Plugin for Microsoft 365 integration - handles document editing in Office 365

import axios from 'axios'

export default {

  getDocExtension(doc) {
    const name = (doc?.name || '').trim()
    const parts = name.split('.')
    if (parts.length < 2) return null
    return parts.pop().toLowerCase()
  },

  isOfficeFile(doc) {
    const ext = this.getDocExtension(doc)
    return ['docx', 'doc', 'xlsx', 'xls'].includes(ext)
  },

  /**
   * Abre una ventana popup para el editor de Microsoft 365.
   * DEBE llamarse SINCRÓNICAMENTE desde un handler @click.native del usuario.
   * Chrome solo permite window.open() con features desde un click nativo del DOM.
   * @returns {Window|null}
   */
  openEditorPopup() {
    return window.open(
      'about:blank',
      '_blank',
      'width=1400,height=900,scrollbars=yes,resizable=yes,toolbar=no,location=yes'
    )
  },

  /**
   * Muestra spinner de carga en la ventana popup
   */
  _showSpinner(win) {
    try {
      win.document.open()
      win.document.write(
        '<html><head><title>Microsoft 365</title></head>' +
        '<body style="display:flex;justify-content:center;align-items:center;height:100vh;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif;background:#f5f5f5">' +
        '<div style="text-align:center">' +
        '<div style="border:3px solid #f3f3f3;border-top:3px solid #0078d4;border-radius:50%;width:40px;height:40px;animation:spin 1s linear infinite;margin:0 auto 16px"></div>' +
        '<p style="color:#666;font-size:16px">Microsoft 365</p>' +
        '</div>' +
        '<style>@keyframes spin{0%{transform:rotate(0)}100%{transform:rotate(360deg)}}</style>' +
        '</body></html>'
      )
      win.document.close()
    } catch (e) {
      // cross-origin: no podemos escribir, pero la ventana sigue abierta
    }
  },

  /**
   * Autentica al usuario reutilizando la MISMA ventana popup.
   * Flujo: popup (spinner) → navega a auth URL → Microsoft login → callback page → postMessage
   * Al recibir postMessage exitoso, navega popup de vuelta a about:blank antes de que
   * el callback la cierre (tiene 1.5s de delay).
   * @param {Window} win - Ventana popup ya abierta
   * @param {Number} documentId
   * @param {Object} vm
   * @returns {Promise<Boolean>}
   */
  _authenticateInWindow(win, documentId, vm) {
    return new Promise((resolve) => {
      const messageHandler = (event) => {
        if (event.origin !== window.location.origin) return
        if (event.data.type !== 'microsoft-auth-callback') return

        window.removeEventListener('message', messageHandler)

        if (event.data.status === 'success') {
          // Navegar inmediatamente a about:blank para evitar que el callback cierre la ventana
          // (el callback tiene setTimeout(window.close, 1500))
          try { win.location.href = 'about:blank' } catch (e) { /* cross-origin */ }
          vm.$buefy.toast.open({
            message: vm.$t('microsoft365.upload_success'),
            type: 'is-success',
            duration: 2000
          })
          resolve(true)
        } else {
          const msg = event.data.status === 'denied'
            ? vm.$t('microsoft_auth_cancelled')
            : (event.data.error || vm.$t('microsoft_auth_error'))
          vm.$buefy.toast.open({
            message: msg,
            type: event.data.status === 'denied' ? 'is-warning' : 'is-danger',
            duration: 3000
          })
          resolve(false)
        }
      }

      window.addEventListener('message', messageHandler)

      // Navegar la popup existente a la URL de auth
      const authUrl = `${window.location.origin}/api/v2/login/azure?documentId=${documentId}`
      win.location.href = authUrl

      vm.$buefy.toast.open({
        message: vm.$t('microsoft_authentication'),
        type: 'is-info',
        duration: 3000
      })

      // Si el usuario cierra la ventana durante el auth, resolver como false
      const checkClosed = setInterval(() => {
        if (win.closed) {
          clearInterval(checkClosed)
          window.removeEventListener('message', messageHandler)
          resolve(false)
        }
      }, 500)
    })
  },

  async syncFromOneDrive(documentId, onSuccess, vm) {
    try {
      vm.$buefy.toast.open({
        message: vm.$t('microsoft365.sync_changes'),
        type: 'is-info',
        duration: 2000
      })

      const { data } = await axios.post(`/api/v2/onedrive/sync/${documentId}`)

      if (data && data.success) {
        vm.$buefy.toast.open({
          message: vm.$t('microsoft365.sync_success'),
          type: 'is-success',
          duration: 3000
        })
        if (onSuccess) onSuccess()
        return true
      } else {
        throw new Error(data?.msg || vm.$t('microsoft365.error_syncing'))
      }
    } catch (error) {
      vm.$buefy.toast.open({
        message: vm.$t('microsoft365.error_syncing') + ': ' + (error.response?.data?.msg || error.message),
        type: 'is-danger',
        duration: 5000
      })
      return false
    }
  },

  async downloadFromOneDriveForValidation(documentId, vm) {
    try {
      vm.$buefy.toast.open({
        message: vm.$t('microsoft365.sync_changes'),
        type: 'is-info',
        duration: 2000
      })

      const response = await axios.get(`/api/v2/onedrive/download/${documentId}`, {
        responseType: 'blob'
      })

      const originalFilename = response.headers['x-original-filename'] || 'document'
      const documentName = response.headers['x-document-name'] || ''
      const contentType = response.headers['content-type'] || 'application/octet-stream'

      const blob = new Blob([response.data], { type: contentType })
      const file = new File([blob], originalFilename, { type: contentType })

      file.documentId = documentId
      file.desc_name = documentName
      file.custom_name = originalFilename
      file.coded_name = 0

      vm.$buefy.toast.open({
        message: vm.$t('microsoft365.sync_success'),
        type: 'is-success',
        duration: 3000
      })

      return file
    } catch (error) {
      vm.$buefy.toast.open({
        message: vm.$t('microsoft365.error_syncing') + ': ' + (error.response?.data?.msg || error.message),
        type: 'is-danger',
        duration: 5000
      })
      return null
    }
  },

  /**
   * Abre documento en Microsoft 365. La popup se reutiliza para todo el flujo:
   * spinner → auth (si necesario) → spinner → editor de Office
   * @param {Object} document - { document_id, name }
   * @param {Window} editorWindow - Popup pre-abierta desde @click.native
   * @param {Function} onSuccess - Callback tras sincronizar
   * @param {Object} vm - Vue instance
   */
  async openWithMicrosoft365(document, editorWindow, onSuccess, vm) {
    this._showSpinner(editorWindow)

    try {
      const { data: authCheck } = await axios.get('/api/v2/microsoft/check-auth')

      if (!authCheck || authCheck.authenticated !== true) {
        // Reusar la MISMA popup para auth (sin cerrar, sin diálogos)
        const authOk = await this._authenticateInWindow(editorWindow, document.document_id, vm)
        if (!authOk) {
          if (!editorWindow.closed) editorWindow.close()
          return
        }
        // Auth OK → mostrar spinner de nuevo y continuar al editor
        this._showSpinner(editorWindow)
      }

      await this._uploadAndOpenEditor(document, editorWindow, onSuccess, vm)

    } catch (error) {
      if (editorWindow && !editorWindow.closed) editorWindow.close()

      const data = error?.response?.data

      if (data?.needsAuth) {
        // Error inesperado de auth: popup ya cerrada → pedir click para reabrir
        this._promptReopenEditor(document, onSuccess, vm)
        return
      }

      vm.$buefy.toast.open({
        message: vm.$t('microsoft_auth_error') + ': ' + (data?.msg || error.message),
        type: 'is-danger',
        duration: 5000
      })
    }
  },

  /**
   * Igual que openWithMicrosoft365 pero para flujo de VALIDACIONES (no guarda en S3).
   */
  async openWithMicrosoft365ForValidation(document, editorWindow, onFileDownloaded, vm) {
    this._showSpinner(editorWindow)

    try {
      const { data: authCheck } = await axios.get('/api/v2/microsoft/check-auth')

      if (!authCheck || authCheck.authenticated !== true) {
        const authOk = await this._authenticateInWindow(editorWindow, document.document_id, vm)
        if (!authOk) {
          if (!editorWindow.closed) editorWindow.close()
          return
        }
        this._showSpinner(editorWindow)
      }

      await this._uploadAndOpenEditorForValidation(document, editorWindow, onFileDownloaded, vm)

    } catch (error) {
      if (editorWindow && !editorWindow.closed) editorWindow.close()

      const data = error?.response?.data

      if (data?.needsAuth) {
        this._promptReopenEditorForValidation(document, onFileDownloaded, vm)
        return
      }

      vm.$buefy.toast.open({
        message: vm.$t('microsoft_auth_error') + ': ' + (data?.msg || error.message),
        type: 'is-danger',
        duration: 5000
      })
    }
  },

  // ——— Métodos internos ———

  /**
   * Muestra diálogo con 3 botones: Editar online | Guardar cambios y cerrar | Cerrar
   */
  _showPostEditDialog(vm, { onEditOnline, onSaveAndClose }) {
      const dialog = vm.$buefy.dialog.confirm({
        title: vm.$t('microsoft365.editing_title'),
        message: vm.$t('microsoft365.select_option'),
        confirmText: vm.$t('microsoft365.edit_online'),
        cancelText: vm.$t('microsoft365.save_and_close'),
        type: 'is-info',
        hasIcon: true,
        icon: 'edit',
        iconPack: 'fas',
        canCancel: ['button'],
        onConfirm: onEditOnline,
        onCancel: onSaveAndClose
      })

      vm.$nextTick(() => {
        if (!dialog?.$el) return

        const footer = dialog.$el.querySelector('.modal-card-foot')
        if (!footer) return

        const buttonsWrap = footer.querySelector('.buttons') || footer

        // Grid limpio
        buttonsWrap.style.display = 'grid'
        buttonsWrap.style.gridTemplateColumns = '1fr 1fr'
        buttonsWrap.style.gap = '12px'
        buttonsWrap.style.width = '100%'
        buttonsWrap.style.margin = '0' // por si .buttons mete margen

        const buttons = Array.from(buttonsWrap.querySelectorAll('button.button'))
        const confirmBtn = buttons.find(b => b.classList.contains('is-info'))
        const cancelBtn = buttons.find(b => b !== confirmBtn)

        // Quitar márgenes que Bulma mete a los botones dentro de .buttons
        buttons.forEach(b => (b.style.margin = '0'))

        // Ambos arriba iguales
        ;[confirmBtn, cancelBtn].forEach(b => {
          if (!b) return
          b.style.width = '100%'
          b.style.display = 'flex'
          b.style.justifyContent = 'center'
          b.style.whiteSpace = 'nowrap'
        })

        if (cancelBtn) cancelBtn.classList.add('is-warning')

        // "Cerrar" abajo full width
        const closeBtn = document.createElement('button')
        closeBtn.className = 'button is-light'
        closeBtn.textContent = vm.$t('close')
        closeBtn.style.border = '1px solid #dbdbdb'
        closeBtn.style.gridColumn = '1 / -1'
        closeBtn.style.width = '100%'
        closeBtn.style.margin = '0'
        closeBtn.addEventListener('click', () => dialog.close())

        buttonsWrap.appendChild(closeBtn)
      })
    },


  /**
   * Fallback: si la popup se cerró inesperadamente y necesita auth,
   * pedir un click para reabrir (gesto fresco para Chrome).
   */
  _promptReopenEditor(document, onSuccess, vm) {
    vm.$buefy.dialog.confirm({
      title: vm.$t('microsoft365.editing_title'),
      message: vm.$t('microsoft365.auth_success_retry'),
      confirmText: vm.$t('edit'),
      cancelText: vm.$t('cancel'),
      type: 'is-info',
      onConfirm: () => {
        const win = this.openEditorPopup()
        if (win) {
          this.openWithMicrosoft365(document, win, onSuccess, vm)
        }
      }
    })
  },

  _promptReopenEditorForValidation(document, onFileDownloaded, vm) {
    vm.$buefy.dialog.confirm({
      title: vm.$t('microsoft365.editing_title'),
      message: vm.$t('microsoft365.auth_success_retry'),
      confirmText: vm.$t('edit'),
      cancelText: vm.$t('cancel'),
      type: 'is-info',
      onConfirm: () => {
        const win = this.openEditorPopup()
        if (win) {
          this.openWithMicrosoft365ForValidation(document, win, onFileDownloaded, vm)
        }
      }
    })
  },

  async _uploadAndOpenEditor(document, editorWindow, onSuccess, vm) {
    vm.$buefy.toast.open({
      message: vm.$t('microsoft365.upload_success'),
      type: 'is-info',
      duration: 2000
    })

    const { data: uploadData } = await axios.post(`/api/v2/onedrive/upload/${document.document_id}`)

    if (!uploadData || !uploadData.success) {
      throw new Error(uploadData?.msg || vm.$t('microsoft365.error_uploading'))
    }

    const editUrl = uploadData.editUrl || uploadData.webUrl
    if (!editUrl) {
      throw new Error(vm.$t('microsoft365.error_uploading_file'))
    }

    editorWindow.location.href = editUrl

    const loadingComponent = vm.$buefy.loading.open({ container: null, canCancel: false })

    vm.$buefy.toast.open({
      message: vm.$t('microsoft365.editing_in_progress'),
      type: 'is-info',
      duration: 5000,
      position: 'is-top'
    })

    const checkWindowClosed = setInterval(() => {
      if (editorWindow.closed) {
        clearInterval(checkWindowClosed)
        loadingComponent.close()

        this._showPostEditDialog(vm, {
          onEditOnline: async () => {
            const win = this.openEditorPopup()
            if (win) this._showSpinner(win)

            const synced = await this.syncFromOneDrive(document.document_id, onSuccess, vm)

            if (synced && win && !win.closed) {
              await this.openWithMicrosoft365(document, win, onSuccess, vm)
            } else if (win && !win.closed) {
              win.close()
            }
          },
          onSaveAndClose: async () => {
            await this.syncFromOneDrive(document.document_id, onSuccess, vm)
          }
        })
      }
    }, 500)
  },

  async _uploadAndOpenEditorForValidation(document, editorWindow, onFileDownloaded, vm) {
    vm.$buefy.toast.open({
      message: vm.$t('microsoft365.upload_success'),
      type: 'is-info',
      duration: 2000
    })

    const { data: uploadData } = await axios.post(`/api/v2/onedrive/upload/${document.document_id}`)

    if (!uploadData || !uploadData.success) {
      throw new Error(uploadData?.msg || vm.$t('microsoft365.error_uploading'))
    }

    const editUrl = uploadData.editUrl || uploadData.webUrl
    if (!editUrl) {
      throw new Error(vm.$t('microsoft365.error_uploading_file'))
    }

    editorWindow.location.href = editUrl

    const loadingComponent = vm.$buefy.loading.open({ container: null, canCancel: false })

    vm.$buefy.toast.open({
      message: vm.$t('microsoft365.editing_in_progress'),
      type: 'is-info',
      duration: 5000,
      position: 'is-top'
    })

    const checkWindowClosed = setInterval(() => {
      if (editorWindow.closed) {
        clearInterval(checkWindowClosed)
        loadingComponent.close()

        this._showPostEditDialog(vm, {
          onEditOnline: async () => {
            const win = this.openEditorPopup()
            if (win) this._showSpinner(win)

            const file = await this.downloadFromOneDriveForValidation(document.document_id, vm)
            if (file && onFileDownloaded) onFileDownloaded(file)

            if (file && win && !win.closed) {
              await this.openWithMicrosoft365ForValidation(document, win, onFileDownloaded, vm)
            } else if (win && !win.closed) {
              win.close()
            }
          },
          onSaveAndClose: async () => {
            const file = await this.downloadFromOneDriveForValidation(document.document_id, vm)
            if (file && onFileDownloaded) onFileDownloaded(file)
          }
        })
      }
    }, 500)
  }
}
